import { Base } from './base-model';
import { Song } from './song';
import {Common} from '../lib/common';

export class LessonSong extends Base {
    name: string
    key: string             // C C# Db D D# Eb E F F# Gb G G# Ab A A# Bb B
    tonality: string        // Major minor dorian phrygian Lydian Mixolydian aeolian Other (NOTE: capitalization is important)
    meter: string           // Duple, Triple, Duple/Triple, Swing, Asymmetric, Other
    startingPitch: string   // C C# Db D D# Eb E F F# Gb G G# Ab A A# Bb B
    isSwow: boolean
    url: string
    karaokeUrl: string
    tags: string
    suggestedActivity: string
    otherInfo: string
    tpName: string
    tpUrl: string
    rpName: string
    rpUrl: string
    tsbUrl: string
    time: string
    songUuid: string        // song.uuid if created from a Song; null for custom song
    activity: string
    hasRp: boolean
    hasTp: boolean
    hasInstrument: boolean
    recordingType: LessonSong.RecordingType
    pemUuid: string

    tagsList(): string[] {
        // return this.tags.splitTrim()  // for some reason isn't working for this class????
        return (this.tags || '').split(',').map((value: string) => value.trim()).filter((value: string) => value.length > 0)
    }

    get formattedTonality(): string {
        const firstTonalityChar = this.tonality.substr(0, 1)
        let tonality = ''
        if (firstTonalityChar === firstTonalityChar.toLowerCase()) {
            tonality = this.key.toLowerCase() + ' ' + this.tonality + (this.startingPitch ? ' (' + this.startingPitch + ')' : '')
        } else {
            tonality = this.key + ' ' + this.tonality + (this.startingPitch ? ' (' + this.startingPitch + ')' : '')
        }
        tonality = tonality.replace('/', '/\n')
        return tonality
    }

    get isMajor(): boolean { return this.tonality && this.tonality.toLowerCase().indexOf('major') >= 0  && this.tonality.toLowerCase().indexOf('minor') < 0 }
    get isNonmajor(): boolean { return this.tonality && !this.isMajor }
    get isDuple(): boolean { return this.meter && this.meter.toLowerCase() === 'duple' }
    get isNonduple(): boolean { return this.meter && !this.isDuple }
    get useRecording(): boolean { return this.recordingType !== LessonSong.RecordingType.None }
    get hasPem(): boolean { return this.pemUuid && this.pemUuid.length > 0 }

    get isHelloSong(): boolean { return this.name.startsWith('Hello') || this.name.startsWith('Hey, Hey, Wha') }
    get isGoodbyeSong(): boolean { return this.name.startsWith('Goodbye') }
    get isDefaultSong(): boolean { return this.name.length === 0 }
    get isTonalPatterns(): boolean { return this.name.startsWith('Tonal Patterns') }
    get isRhythmPatterns(): boolean { return this.name.startsWith('Rhythm Patterns') }

    get canPlay(): boolean {
        if (this.isCustom) {
            return this.recordingType === LessonSong.RecordingType.Custom
        }
        return this.recordingType === LessonSong.RecordingType.Original || this.recordingType === LessonSong.RecordingType.Karaoke
    }

    static defaults(): LessonSong {
        const lessonSong = new LessonSong()
        lessonSong.uuid = null
        lessonSong.name = ''
        lessonSong.key = ''
        lessonSong.tonality = ''
        lessonSong.meter = ''
        lessonSong.startingPitch = ''
        lessonSong.isSwow = false
        lessonSong.url = ''
        lessonSong.karaokeUrl = ''
        lessonSong.tags = ''
        lessonSong.suggestedActivity = ''
        lessonSong.otherInfo = ''
        lessonSong.tpName = ''
        lessonSong.tpUrl = ''
        lessonSong.rpName = ''
        lessonSong.rpUrl = ''
        lessonSong.tsbUrl = ''
        lessonSong.time = ''
        lessonSong.activity = ''
        lessonSong.hasRp = false
        lessonSong.hasTp = false
        lessonSong.hasInstrument = false
        lessonSong.recordingType = LessonSong.RecordingType.None
        lessonSong.pemUuid = ''
        return lessonSong
    }

    static createFrom(song: Song): LessonSong {
        const lessonSong = new LessonSong()
        lessonSong.name = song.name
        lessonSong.key = song.key
        lessonSong.tonality = song.tonality
        lessonSong.meter = song.meter
        lessonSong.startingPitch = song.startingPitch
        lessonSong.isSwow = song.isSwow
        lessonSong.url = song.url
        lessonSong.karaokeUrl = song.karaokeUrl
        lessonSong.tags = song.tags
        lessonSong.suggestedActivity = song.suggestedActivity
        lessonSong.otherInfo = song.otherInfo
        lessonSong.tpName = song.tpName
        lessonSong.tpUrl = song.tpUrl
        lessonSong.rpName = song.rpName
        lessonSong.rpUrl = song.rpUrl
        lessonSong.tsbUrl = song.tsbUrl
        lessonSong.time = song.time
        lessonSong.songUuid = song.uuid
        lessonSong.activity = ''
        lessonSong.hasRp = false
        lessonSong.hasTp = false
        lessonSong.hasInstrument = false
        lessonSong.recordingType = LessonSong.RecordingType.None
        lessonSong.pemUuid = ''
        return lessonSong
    }

    copyFrom(lessonSong: LessonSong) {
        super.copyFrom(lessonSong)
        this.name = lessonSong.name
        this.key = lessonSong.key
        this.tonality = lessonSong.tonality
        this.meter = lessonSong.meter
        this.startingPitch = lessonSong.startingPitch
        this.isSwow = lessonSong.isSwow
        this.url = lessonSong.url
        this.karaokeUrl = lessonSong.karaokeUrl
        this.tags = lessonSong.tags
        this.suggestedActivity = lessonSong.suggestedActivity
        this.otherInfo = lessonSong.otherInfo
        this.tpName = lessonSong.tpName
        this.tpUrl = lessonSong.tpUrl
        this.rpName = lessonSong.rpName
        this.rpUrl = lessonSong.rpUrl
        this.tsbUrl = lessonSong.tsbUrl
        this.time = lessonSong.time
        this.songUuid = lessonSong.songUuid
        this.activity = lessonSong.activity
        this.hasRp = lessonSong.hasRp
        this.hasTp = lessonSong.hasTp
        this.hasInstrument = lessonSong.hasInstrument
        this.recordingType = lessonSong.recordingType
        this.pemUuid = lessonSong.pemUuid
    }

    unsavedCopy(): LessonSong {
        const lessonSong = super.unsavedCopy() as LessonSong
        return lessonSong
    }

    get isCustom(): boolean {
        return Common.isNullOrUndefined(this.songUuid)
    }
}

// https://stackoverflow.com/questions/29844959/enum-inside-class-typescript-definition-file
export namespace LessonSong {
    export enum RecordingType {
        None = '',
        Original = 'original',
        Karaoke = 'karaoke',
        Custom = 'custom'
    }
}
